import type { SubjectResource } from "api/generated";
import type { ConversationSubjectOption } from "conversations/types";

/**
 * Maps a subject from `api/portfolio/subjects` to the option the gesprek forms show.
 *
 * The name comes from `synonym_groups` through a LEFT join, so a shortage without a
 * synonym row has none — those subjects fall back to the shortage rather than rendering
 * as a blank row the teacher cannot identify.
 */
export const conversationSubjectFromDto = (
  dto: SubjectResource,
): ConversationSubjectOption => ({
  shortage: dto.shortage,
  label: dto.name ?? dto.shortage,
});

/**
 * Two shortages can share one name: they map to the same `synonym_groups` row, so e.g.
 * both `BSM` and `LO` come back as "Gym". Rendered plain, the teacher sees the same option
 * twice and cannot tell which subject they are picking, so a shared label is suffixed with
 * its shortage.
 *
 * Sorted on the label the docent actually reads, and only here: the endpoint orders on the
 * synonym-group name, which is null for a vak without one — those sort to the front and
 * then render their afkorting, so the list arrives looking unsorted. Suffixing first, so
 * "Gym (BSM)" and "Gym (LO)" land in the order they are shown.
 */
export const conversationSubjectsFromDto = (
  dtos: SubjectResource[],
): ConversationSubjectOption[] => {
  const options = dtos.map(conversationSubjectFromDto);

  const labelCounts = options.reduce<Record<string, number>>(
    (counts, option) => ({
      ...counts,
      [option.label]: (counts[option.label] ?? 0) + 1,
    }),
    {},
  );

  return options
    .map((option) =>
      labelCounts[option.label] > 1 && option.label !== option.shortage
        ? { ...option, label: `${option.label} (${option.shortage})` }
        : option,
    )
    .sort((first, second) => first.label.localeCompare(second.label, "nl"));
};
